/**
 * @fileoverview ClubOS Youth Academy Dashboard
 * Real-time KPIs: players, attendance, fees, injuries, batch utilization.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftProgress from "components/SoftProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import Table from "examples/Tables/Table";

import PropTypes from "prop-types";

import { academyService } from "api";
import FootballLoader from "components/FootballLoader";
import { TYPOGRAPHY, BACKGROUNDS, COLORS } from "config/branding";

import typography from "assets/theme/base/typography";

const AGE_GROUP_ORDER = ["U8", "U10", "U12", "U14", "U16", "U18", "Senior"];

// ── Clickable KPI card wrapper ────────────────────────────────────────────────
function ClickableStatisticsCard({ route, ...props }) {
  const navigate = useNavigate();
  return (
    <div onClick={() => navigate(route)} style={{ cursor: "pointer" }}>
      <MiniStatisticsCard {...props} />
    </div>
  );
}
ClickableStatisticsCard.propTypes = { route: PropTypes.string.isRequired };

// ── Main component ────────────────────────────────────────────────────────────
function AcademyDashboard() {
  const { size } = typography;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    academyService.getDashboard()
      .then((response) => {
        if (response && typeof response === "object") {
          const normalized = {
            totalPlayers: response.totalPlayers ?? 0,
            activePlayers: response.activePlayers ?? 0,
            trialPlayers: response.trialPlayers ?? 0,
            inactivePlayers: response.inactivePlayers ?? 0,
            totalBatches: response.totalBatches ?? 0,
            activeBatches: response.activeBatches ?? 0,
            totalCoaches: response.totalCoaches ?? 0,
            todayAttendancePct: response.todayAttendancePct ?? 0,
            last30DaysAttendancePct: response.last30DaysAttendancePct ?? 0,
            last30DaysCoachAttendancePct: response.last30DaysCoachAttendancePct ?? 0,
            thisMonthCollected: response.thisMonthCollected ?? 0,
            thisMonthPending: response.thisMonthPending ?? 0,
            thisMonthTotal: response.thisMonthTotal ?? 0,
            thisMonthExpenses: response.thisMonthExpenses ?? 0,
            thisMonthRevenue: response.thisMonthRevenue ?? 0,
            collectionRate: response.collectionRate ?? 0,
            activeInjuries: response.activeInjuries ?? 0,
            clearedThisMonth: response.clearedThisMonth ?? 0,
            batchUtilization: Array.isArray(response.batchUtilization) ? response.batchUtilization : [],
            monthlyRevenue: Array.isArray(response.monthlyRevenue) ? response.monthlyRevenue : [],
            monthlyExpenses: Array.isArray(response.monthlyExpenses) ? response.monthlyExpenses : [],
            monthlyAttendance: Array.isArray(response.monthlyAttendance) ? response.monthlyAttendance : [],
            activeInjuryList: Array.isArray(response.activeInjuryList) ? response.activeInjuryList : [],
            topDefaulters: Array.isArray(response.topDefaulters) ? response.topDefaulters : [],
          };
          setData(normalized);
        } else {
          setError("Invalid response format");
        }
      })
      .catch((e) => { console.error(e); setError("Unable to load academy data"); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <FootballLoader label="Loading academy dashboard…" minHeight={200} />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <SoftTypography variant="h6" color="error">{error ?? "No data available"}</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  // ── Derived values ──────────────────────────────────────────────────────────
  const collectionRate = Math.round(data.collectionRate ?? 0);
  const collectionColor = collectionRate >= 85 ? "success" : collectionRate >= 60 ? "warning" : "error";
  const batchUtil = data.batchUtilization ?? [];
  const defaulters = [...(data.topDefaulters ?? [])].sort((a, b) => (b.outstanding ?? 0) - (a.outstanding ?? 0));

  // ── Fees line chart ─────────────────────────────────────────────────────────
  const revenueMonths = data.monthlyRevenue ?? [];
  const expensesByMonth = {};
  (data.monthlyExpenses ?? []).forEach((e) => { expensesByMonth[e.month] = e.amount ?? 0; });

  const feesLineChart = {
    labels: revenueMonths.map((m) => m.month),
    datasets: [
      { label: "Income from Fees", color: "primary",  data: revenueMonths.map((m) => m.collected ?? 0) },
      { label: "Expenses",         color: "dark",  data: revenueMonths.map((m) => expensesByMonth[m.month] ?? m.expenses ?? 0) },
    ],
  };

  // ── Attendance bar chart ────────────────────────────────────────────────────
  const studentPresentPct = Math.round(data.last30DaysAttendancePct ?? 0);
  const studentAbsentPct  = Math.max(0, 100 - studentPresentPct);
  const coachPresentPct   = Math.round(data.last30DaysCoachAttendancePct ?? 0);
  const coachAbsentPct    = Math.max(0, 100 - coachPresentPct);

  const attendanceBarChart = {
    labels: (data.monthlyAttendance ?? []).map((m) => m.month),
    datasets: {
      label: "Attendance %",
      data: (data.monthlyAttendance ?? []).map((m) => Math.round(m.studentPresentPct ?? m.presentPct ?? 0)),
    },
  };

  const attendanceItems = [
    { icon: { color: "primary", component: "people"      }, label: "students present", progress: { content: `${studentPresentPct}%`, percentage: studentPresentPct } },
    { icon: { color: "error",   component: "person_off"  }, label: "students absent",  progress: { content: `${studentAbsentPct}%`,  percentage: studentAbsentPct  } },
    { icon: { color: "success", component: "sports"      }, label: "coaches present",  progress: { content: `${coachPresentPct}%`,  percentage: coachPresentPct  } },
    { icon: { color: "warning", component: "sports_off"  }, label: "coaches absent",   progress: { content: `${coachAbsentPct}%`,   percentage: coachAbsentPct   } },
  ];

  // ── Batch utilization table (grouped by age group) ──────────────────────────
  const batchByAge = {};
  batchUtil.forEach((b) => {
    const ag = b.ageGroup ?? "Unknown";
    if (!batchByAge[ag]) batchByAge[ag] = { batches: 0, enrolled: 0, capacity: 0 };
    batchByAge[ag].batches++;
    batchByAge[ag].enrolled += b.enrolled ?? 0;
    batchByAge[ag].capacity += b.capacity ?? 0;
  });

  const batchColumns = [
    { name: "age group",   align: "center" },
    { name: "batches",     align: "center" },
    { name: "students",    align: "center" },
    { name: "utilization", align: "center" },
  ];

  const batchRows = [...AGE_GROUP_ORDER, ...Object.keys(batchByAge).filter((ag) => !AGE_GROUP_ORDER.includes(ag))]
    .filter((ag) => batchByAge[ag])
    .map((ag) => {
      const g = batchByAge[ag];
      const pct = g.capacity > 0 ? Math.round((g.enrolled / g.capacity) * 100) : 0;
      const progColor = pct >= 90 ? "error" : pct >= 70 ? "warning" : "success";
      return {
        "age group": (
          <SoftTypography variant="caption" fontWeight="medium" color="dark">
            {ag}
          </SoftTypography>
        ),
        batches: (
          <SoftTypography variant="caption" color="text" fontWeight="medium">
            {g.batches}
          </SoftTypography>
        ),
        students: (
          <SoftTypography variant="caption" color="text" fontWeight="medium">
            {g.enrolled}
          </SoftTypography>
        ),
        utilization: (
          <SoftBox width="8rem" textAlign="left">
            <SoftProgress value={pct} color={progColor} variant="gradient" label={false} />
            <SoftTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
              {g.enrolled}/{g.capacity} ({pct}%)
            </SoftTypography>
          </SoftBox>
        ),
      };
    });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" sx={{ color: TYPOGRAPHY.page.titleColor }}>
              Youth Development Programme Dashboard
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: TYPOGRAPHY.page.subtitleColor }}>
              Click on the cards for detailed reports and management tools
            </SoftTypography>
          </SoftBox>
          <Chip
            icon={<Icon sx={{ fontSize: "1rem !important", color: "white !important" }}>school</Icon>}
            label={`${data.activePlayers} Active Students`}
            size="small"
            sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600 }}
          />
        </SoftBox>

        {/* ── Top KPI row ──────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableStatisticsCard
                route="/academy/fees"
                title={{ text: "Fee Collection" }}
                count={`${collectionRate}%`}
                percentage={{ color: collectionColor, text: `₹${Number(data.thisMonthPending).toLocaleString("en-IN")} Due` }}
                icon={{ color: "primary", component: "payments" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableStatisticsCard
                route="/academy/attendance"
                title={{ text: "Attendance (30d)" }}
                count={`${data.last30DaysAttendancePct}%`}
                percentage={{ color: data.last30DaysAttendancePct >= 75 ? "success" : "warning", text: `today ${data.todayAttendancePct}%` }}
                icon={{ color: "primary", component: "fact_check" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableStatisticsCard
                route="/academy/batches"
                title={{ text: "Active Batches" }}
                count={String(data.activeBatches)}
                percentage={{ color: "primary", text: `${data.totalCoaches} Coaches` }}
                icon={{ color: "primary", component: "sports_soccer" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ClickableStatisticsCard
                route="/academy/fees"
                title={{ text: "Net Revenue" }}
                count={`₹${Number(data.thisMonthRevenue).toLocaleString("en-IN")}`}
                percentage={{ color: data.thisMonthRevenue >= 0 ? "success" : "error", text: `Inc. − Exp.` }}
                icon={{ color: "primary", component: "account_balance_wallet" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Attendance Bar Chart + Fees Line Chart (ref-ui style) ─────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <ReportsBarChart
                color="primary"
                bgGradient={BACKGROUNDS.sidenavGradient}
                title="Attendance Overview"
                description={
                  <SoftBox display="flex" alignItems="center">
                    <SoftBox fontSize={size.lg} color="success" mb={0.3} mr={0.5} lineHeight={0}>
                      <Icon className="font-bold">fact_check</Icon>
                    </SoftBox>
                    <SoftTypography
                      variant="button"
                      sx={{ color: `rgba(${COLORS.primary.rgb}, 1)` }}
                      fontWeight="medium"
                    >
                      {studentPresentPct}%{" "}
                      <SoftTypography
                        variant="button"
                        sx={{ color: `rgba(${COLORS.primary.rgb}, 0.7)` }}
                        fontWeight="regular"
                      >
                        last 30 days
                      </SoftTypography>
                    </SoftTypography>
                  </SoftBox>
                }
                chart={attendanceBarChart}
                items={attendanceItems}
              />
            </Grid>
            <Grid item xs={12} lg={7}>
              <GradientLineChart
                title="Monthly Fee Collection"
                description={
                  <SoftBox display="flex" alignItems="center">
                    <SoftBox fontSize={size.lg} color={collectionColor} mb={0.3} mr={0.5} lineHeight={0}>
                      <Icon className="font-bold">
                        {collectionRate >= 85 ? "arrow_upward" : collectionRate >= 60 ? "remove" : "arrow_downward"}
                      </Icon>
                    </SoftBox>
                    <SoftTypography variant="button" color="text" fontWeight="medium">
                      {collectionRate}% collected{" "}
                      <SoftTypography variant="button" color="text" fontWeight="regular">
                        this month
                      </SoftTypography>
                    </SoftTypography>
                  </SoftBox>
                }
                height="20.25rem"
                chart={feesLineChart}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Batch Utilization Table + Fee Defaulters ─────────────────────── */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={8}>
            <Card>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                <SoftBox>
                  <SoftTypography variant="h6" gutterBottom>
                    Batch Utilization
                  </SoftTypography>
                  <SoftBox display="flex" alignItems="center" lineHeight={0}>
                    <Icon sx={{ fontWeight: "bold", color: ({ palette: { info } }) => info.main, mt: -0.5 }}>
                      groups
                    </Icon>
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                      &nbsp;<strong>{batchUtil.length} batches</strong> configured
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
              <SoftBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {batchRows.length > 0 ? (
                  <Table columns={batchColumns} rows={batchRows} />
                ) : (
                  <SoftBox p={3}>
                    <SoftTypography variant="caption" color="text">No batches configured</SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: "100%" }}>
              <SoftBox pt={3} px={3}>
                <SoftTypography variant="h6" fontWeight="medium">
                  Fee Defaulters
                </SoftTypography>
                <SoftBox mt={1} mb={2}>
                  {defaulters.length > 0 ? (
                    <SoftTypography variant="button" color="text" fontWeight="regular">
                      <SoftTypography display="inline" variant="body2" verticalAlign="middle">
                        <Icon sx={{ fontWeight: "bold", color: ({ palette: { error } }) => error.main }}>
                          warning
                        </Icon>
                      </SoftTypography>
                      &nbsp;
                      <SoftTypography variant="button" color="text" fontWeight="medium">
                        {defaulters.length}
                      </SoftTypography>{" "}
                      students with overdue fees
                    </SoftTypography>
                  ) : (
                    <SoftTypography variant="button" color="text" fontWeight="regular">
                      <SoftTypography display="inline" variant="body2" verticalAlign="middle">
                        <Icon sx={{ fontWeight: "bold", color: ({ palette: { success } }) => success.main }}>
                          check_circle
                        </Icon>
                      </SoftTypography>
                      &nbsp;No outstanding dues
                    </SoftTypography>
                  )}
                </SoftBox>
              </SoftBox>
              <SoftBox p={2}>
                {defaulters.slice(0, 8).map((d, idx) => (
                  <SoftBox
                    key={d.playerId ?? idx}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={0.75}
                    px={1}
                    sx={{
                      borderBottom: idx < Math.min(defaulters.length, 8) - 1 ? "1px solid #f0f0f0" : "none",
                    }}
                  >
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="medium" color="dark" display="block" lineHeight={1.3}>
                        {d.playerName}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text">{d.batchName}</SoftTypography>
                    </SoftBox>
                    <SoftBox textAlign="right">
                      <SoftTypography variant="caption" fontWeight="bold" color="error" display="block" lineHeight={1.3}>
                        ₹{Number(d.outstanding).toLocaleString("en-IN")}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text">{d.monthsOverdue}m overdue</SoftTypography>
                    </SoftBox>
                  </SoftBox>
                ))}
                {defaulters.length === 0 && (
                  <SoftBox display="flex" alignItems="center" gap={1} mt={1} px={1}>
                    <SoftTypography variant="caption" color="text">All fees are up to date</SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan Sporting Club" }} links={[]} />
    </DashboardLayout>
  );
}

export default AcademyDashboard;
